import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Compass, Check, Loader2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

interface JobStatus {
  job_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message?: string | null;
}

interface NorthLoadingProgressProps {
  northId: string;
  onComplete?: () => void;
  onDismiss?: () => void;
  initialTimestamps?: {
    executive_summary_updated_at?: string | null;
    comps_summary_updated_at?: string | null;
    neighborhood_summary_updated_at?: string | null;
    showings_summary_updated_at?: string | null;
  };
}

const JOB_DISPLAY_NAMES: Record<string, string> = {
  'scraping_property': 'Gathering property data',
  'fetch_comps': 'Finding comparable properties',
  'generate_comps_summary': 'Analyzing comparable properties',
  'fetch_floorplan_tour': 'Fetching floorplan and tour data',
  'fetch_neighborhood_data': 'Analyzing neighborhood intelligence',
  'generate_neighborhood_summary': 'Generating neighborhood insights',
  'generate_executive_summary': 'Generating executive summary',
  'generate_showings_summary': 'Updating showings summary',
};

const JOB_ORDER = [
  'scraping_property', // This is a pseudo-job for the initial scraping phase
  'fetch_comps',
  'generate_comps_summary',
  'fetch_floorplan_tour',
  'fetch_neighborhood_data',
  'generate_neighborhood_summary',
  'generate_executive_summary',
  'generate_showings_summary',
];

export function NorthLoadingProgress({ northId, onComplete, onDismiss, initialTimestamps }: NorthLoadingProgressProps) {
  const [jobs, setJobs] = useState<JobStatus[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [completedJobTypes, setCompletedJobTypes] = useState<Set<string>>(new Set());
  const [scrapingComplete, setScrapingComplete] = useState(false);
  const maxJobsSeenRef = useRef(0);
  const onCompleteCalledRef = useRef(false); // Guard to prevent duplicate onComplete calls
  const isCompleteRef = useRef(false); // Ref to track completion without causing re-renders
  const hasTimedOutRef = useRef(false); // Ref to check timeout without dependency
  const [summariesUpdated, setSummariesUpdated] = useState({
    executive: false,
    comps: false,
    neighborhood: false,
  });

  // Track when we last saw pending jobs, to trigger processor kicks
  const pendingJobsSinceRef = useRef<number | null>(null);
  const lastProcessorKickRef = useRef<number>(0);

  // Guard against premature completion: the dialog opens before is_processing=true is set
  // (scrape-zillow runs first). Old completed jobs from the previous refresh would otherwise
  // satisfy all completion conditions immediately. Only allow onComplete once we've actually
  // seen is_processing flip to true during this dialog's lifetime.
  const hasSeenProcessingRef = useRef(false);

  // 5-minute polling timeout — prevents infinite spinner if backend never resolves
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isCompleteRef.current) {
        hasTimedOutRef.current = true;
        setHasTimedOut(true);
        logger.warn('NorthLoadingProgress timed out after 5 minutes for North:', northId);
      }
    }, 5 * 60 * 1000);

    return () => clearTimeout(timeout);
  }, [northId]);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    const pollJobs = async () => {
      // Stop polling if already complete or timed out (use refs to avoid dependency)
      if (isCompleteRef.current || hasTimedOutRef.current) {
        return;
      }
      
      try {
        // Fetch both jobs and north timestamps
        const [jobsResponse, northResponse] = await Promise.all([
          supabase
            .from('north_processing_jobs' as any)
            .select('job_type, status, error_message')
            .eq('north_id', northId)
            .order('priority', { ascending: false }),
          supabase
            .from('norths')
            .select('executive_summary_updated_at, comps_summary_updated_at, neighborhood_summary_updated_at, neighborhood_data_updated_at, is_processing')
            .eq('id', northId)
            .single()
        ]);

        if (jobsResponse.error) {
          logger.error('Error polling jobs:', jobsResponse.error);
          // If we can't fetch jobs due to RLS, check if north is no longer processing
          if (northResponse.data && !(northResponse.data as any).is_processing && !isCompleteRef.current && hasSeenProcessingRef.current) {
            logger.log('North is no longer processing, marking as complete despite job fetch error');
            isCompleteRef.current = true;
            setIsComplete(true);
            setProgress(100);
            if (onComplete && !onCompleteCalledRef.current) {
              onCompleteCalledRef.current = true;
              onComplete();
            }
          }
          return;
        }

        if (jobsResponse.data) {
          const currentJobs = (jobsResponse.data as unknown) as JobStatus[];
          setJobs(currentJobs);

          // Active retry: if we detect pending jobs that haven't progressed, kick the processor
          // This catches cases where the DB trigger didn't fire or the processor missed the job
          const hasPendingJobs = currentJobs.some(j => j.status === 'pending');
          const hasProcessingJobs = currentJobs.some(j => j.status === 'processing');

          if (hasPendingJobs && !hasProcessingJobs) {
            // Jobs are pending but nothing is processing — processor may not be running
            if (!pendingJobsSinceRef.current) {
              pendingJobsSinceRef.current = Date.now();
            }
            const pendingDuration = Date.now() - pendingJobsSinceRef.current;
            const timeSinceLastKick = Date.now() - lastProcessorKickRef.current;

            // If pending for >10 seconds and we haven't kicked in the last 30 seconds, invoke the processor
            if (pendingDuration > 10_000 && timeSinceLastKick > 30_000) {
              lastProcessorKickRef.current = Date.now();
              logger.log('Kicking process-north-jobs: pending jobs detected with no active processing');
              fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-north-jobs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
              }).catch(err => logger.error('Error kicking processor:', err));
            }
          } else {
            // Reset pending tracker when jobs are progressing
            pendingJobsSinceRef.current = null;
          }

          // If no jobs exist but north is not processing, consider it complete
          // But only do this once to avoid infinite loops
          if (currentJobs.length === 0 && northResponse.data && !(northResponse.data as any).is_processing && !isCompleteRef.current && hasSeenProcessingRef.current) {
            logger.log('No jobs found and north is not processing, marking as complete');
            isCompleteRef.current = true;
            setIsComplete(true);
            setProgress(100);
            if (onComplete && !onCompleteCalledRef.current) {
              onCompleteCalledRef.current = true;
              onComplete();
            }
            return;
          }

          // Update completed job types - once a job is complete, it stays complete
          // Create updated set locally first so we can use it in calculations
          const updatedCompletedJobTypes = new Set(completedJobTypes);
          let hasNewCompletedJobs = false;
          currentJobs.forEach(job => {
            if (job.status === 'completed' || job.status === 'failed') {
              if (!updatedCompletedJobTypes.has(job.job_type)) {
                hasNewCompletedJobs = true;
              updatedCompletedJobTypes.add(job.job_type);
              }
            }
          });
          // Only update state if there are new completed jobs
          if (hasNewCompletedJobs) {
          setCompletedJobTypes(updatedCompletedJobTypes);
          }

          // Mark scraping as complete once jobs appear (and keep it complete)
          const currentScrapingComplete = scrapingComplete || currentJobs.length > 0;
          if (currentScrapingComplete && !scrapingComplete) {
            setScrapingComplete(true);
          }

          // Check if summaries have been updated since we started
          if (northResponse.data && initialTimestamps) {
            const current = northResponse.data as any;

            const execUpdated = !!(current.executive_summary_updated_at &&
              current.executive_summary_updated_at !== initialTimestamps.executive_summary_updated_at);
            const compsUpdated = !!(current.comps_summary_updated_at &&
              current.comps_summary_updated_at !== initialTimestamps.comps_summary_updated_at);
            const neighborhoodUpdated = !!(current.neighborhood_summary_updated_at &&
              current.neighborhood_summary_updated_at !== initialTimestamps.neighborhood_summary_updated_at);

            // Also check if summary content exists (fallback if timestamp check fails)
            const execHasContent = !!(current.executive_summary && current.executive_summary.trim());
            const compsHasContent = !!(current.comps_summary && current.comps_summary.trim());
            const neighborhoodHasContent = !!(current.neighborhood_summary && current.neighborhood_summary.trim());

            // Update if timestamp changed OR if content exists (for cases where timestamp wasn't set)
            const finalExecUpdated = execUpdated || (execHasContent && !summariesUpdated.executive);
            const finalCompsUpdated = compsUpdated || (compsHasContent && !summariesUpdated.comps);
            const finalNeighborhoodUpdated = neighborhoodUpdated || (neighborhoodHasContent && !summariesUpdated.neighborhood);

            // Only update state if values actually changed
            if (finalExecUpdated !== summariesUpdated.executive || 
                finalCompsUpdated !== summariesUpdated.comps || 
                finalNeighborhoodUpdated !== summariesUpdated.neighborhood) {
            setSummariesUpdated({
                executive: finalExecUpdated,
                comps: finalCompsUpdated,
                neighborhood: finalNeighborhoodUpdated,
            });
            }

            // Log summary update status for debugging
            if (execUpdated || compsUpdated || neighborhoodUpdated) {
              logger.debug('📊 Progress: Summary timestamps updated:', {
                executive: execUpdated,
                comps: compsUpdated,
                neighborhood: neighborhoodUpdated,
                executiveTimestamp: current.executive_summary_updated_at,
                initialExecutiveTimestamp: initialTimestamps.executive_summary_updated_at
            });
            }
          }
          
          // Also check if neighborhood_data has been updated (separate from summary)
          const hasNeighborhoodDataJob = updatedCompletedJobTypes.has('fetch_neighborhood_data') || 
            currentJobs.some(j => j.job_type === 'fetch_neighborhood_data');
          // Check if the neighborhood_data job failed
          const neighborhoodDataJobFailed = currentJobs.some(j => 
            j.job_type === 'fetch_neighborhood_data' && j.status === 'failed'
          );
          // Check if neighborhood_data exists and was updated (job completed means data should exist)
          // If job failed, consider it "done" so we don't block completion forever
          const neighborhoodDataUpdated = hasNeighborhoodDataJob 
            ? ((northResponse.data as any)?.neighborhood_data_updated_at != null || neighborhoodDataJobFailed)
            : true; // If no job, don't block completion

          // Calculate progress based on actual jobs that exist
          // Get the set of job types that actually exist (from current jobs)
          const existingJobTypes = new Set(currentJobs.map(j => j.job_type));
          
          // Track the maximum number of jobs we've seen to prevent total from shrinking
          if (existingJobTypes.size > maxJobsSeenRef.current) {
            maxJobsSeenRef.current = existingJobTypes.size;
          }
          
          // Use a stable total: scraping (1) + max jobs we've seen
          const scrapingCount = currentScrapingComplete ? 1 : 0;
          const total = scrapingCount + maxJobsSeenRef.current;
          
          // Determine which summaries we're waiting for based on job types that actually exist
          // Executive summary IS now a job (generate_executive_summary), so check for it
          const hasExecutiveSummaryJob = updatedCompletedJobTypes.has('generate_executive_summary') || 
            currentJobs.some(j => j.job_type === 'generate_executive_summary');
          const hasCompsSummaryJob = updatedCompletedJobTypes.has('generate_comps_summary') || 
            currentJobs.some(j => j.job_type === 'generate_comps_summary');
          const hasNeighborhoodSummaryJob = updatedCompletedJobTypes.has('generate_neighborhood_summary') || 
            currentJobs.some(j => j.job_type === 'generate_neighborhood_summary');

          // Check if summaries are actually done (not just jobs completed)
          const executiveSummaryDone = !hasExecutiveSummaryJob || (summariesUpdated.executive && updatedCompletedJobTypes.has('generate_executive_summary'));
          const compsSummaryDone = !hasCompsSummaryJob || (summariesUpdated.comps && updatedCompletedJobTypes.has('generate_comps_summary'));
          const neighborhoodSummaryDone = !hasNeighborhoodSummaryJob || (summariesUpdated.neighborhood && updatedCompletedJobTypes.has('generate_neighborhood_summary'));
          
          // Count completed jobs - only count jobs that we've seen (either currently exist or were completed)
          // Combine existing jobs and completed job types to get all jobs we've ever seen
          const allSeenJobTypes = new Set([...existingJobTypes, ...updatedCompletedJobTypes]);
          
          // For progress calculation, we need to give partial credit to summary jobs
          // Summary jobs get 75% credit when the job is done but summary not saved yet
          // This allows progress to gradually increase instead of jumping to 95%
          let jobsCompleted = 0;
          let summaryJobsPartial = 0;
          
          for (const jobType of allSeenJobTypes) {
            if (updatedCompletedJobTypes.has(jobType)) {
              const isSummaryJob = jobType === 'generate_executive_summary' || 
                                   jobType === 'generate_comps_summary' || 
                                   jobType === 'generate_neighborhood_summary';
              
              if (isSummaryJob) {
                // Check if summary is actually updated
                let summaryUpdated = false;
                if (jobType === 'generate_executive_summary') {
                  summaryUpdated = summariesUpdated.executive;
                } else if (jobType === 'generate_comps_summary') {
                  summaryUpdated = summariesUpdated.comps;
                } else if (jobType === 'generate_neighborhood_summary') {
                  summaryUpdated = summariesUpdated.neighborhood;
                }
                
                if (summaryUpdated) {
                  // Summary is fully done - count as 1.0
                  jobsCompleted += 1;
                } else {
                  // Job is done but summary not saved yet - give 75% credit
                  summaryJobsPartial += 0.75;
                }
              } else {
                // Non-summary job - count as fully complete
                jobsCompleted += 1;
              }
            }
          }
          
          // Determine if we have any summary jobs (used for completion logic below)
          const hasAnySummaryJobs = hasExecutiveSummaryJob || hasCompsSummaryJob || hasNeighborhoodSummaryJob;

          // Check if all existing jobs are done AND summaries have been updated
          // Only check jobs that actually exist (or have existed)
          const allSeenJobTypesForCompletion = new Set([...existingJobTypes, ...updatedCompletedJobTypes]);

          // If no jobs exist at all, consider jobs "done" if north is ready
          // This handles the case where jobs failed to create due to RLS
          const isNorthReady = northResponse.data && (northResponse.data as any).is_processing === false;
          const noJobsExist = allSeenJobTypesForCompletion.size === 0;

          // Track whether is_processing has been true during this dialog's lifetime.
          // The dialog opens before scrape-zillow finishes (so before is_processing=true is set),
          // meaning old completed jobs + is_processing=false would otherwise fire onComplete
          // immediately on the first poll. Only allow completion once we've seen it be true.
          if ((northResponse.data as any).is_processing === true) {
            hasSeenProcessingRef.current = true;
          }
          
          const allJobsDone = noJobsExist 
            ? isNorthReady // If no jobs, consider done when north is ready
            : (allSeenJobTypesForCompletion.size > 0 && 
                Array.from(allSeenJobTypesForCompletion).every(jobType => 
                  updatedCompletedJobTypes.has(jobType)
                ) && currentScrapingComplete);

          // IMPORTANT: Consider summaries done when:
          // 1. The job is completed (or doesn't exist)
          // 2. AND either:
          //    - The timestamp has been updated (summary saved to DB), OR
          //    - The summary content exists (fallback for cases where timestamp wasn't set)
          // This ensures we wait for the actual OpenAI API call to complete and save, not just job status
          // Also check if north is no longer processing as a final fallback
          const executiveJobDone = !hasExecutiveSummaryJob || updatedCompletedJobTypes.has('generate_executive_summary');
          const compsJobDone = !hasCompsSummaryJob || updatedCompletedJobTypes.has('generate_comps_summary');
          const neighborhoodJobDone = !hasNeighborhoodSummaryJob || updatedCompletedJobTypes.has('generate_neighborhood_summary');
          
          // Check if summary content exists (fallback)
          const execHasContent = northResponse.data && !!(northResponse.data as any).executive_summary && (northResponse.data as any).executive_summary.trim();
          const compsHasContent = northResponse.data && !!(northResponse.data as any).comps_summary && (northResponse.data as any).comps_summary.trim();
          const neighborhoodHasContent = northResponse.data && !!(northResponse.data as any).neighborhood_summary && (northResponse.data as any).neighborhood_summary.trim();
          
          const executiveDone = !hasExecutiveSummaryJob || (executiveJobDone && (summariesUpdated.executive || execHasContent));
          const compsDone = !hasCompsSummaryJob || (compsJobDone && (summariesUpdated.comps || compsHasContent));
          const neighborhoodDone = !hasNeighborhoodSummaryJob || (neighborhoodJobDone && (summariesUpdated.neighborhood || neighborhoodHasContent));
          
          // Also ensure neighborhood_data has been fetched if there was a job for it
          // If the job failed, consider it "done" so we don't block completion forever
          const neighborhoodDataDone = !hasNeighborhoodDataJob || neighborhoodDataUpdated || isNorthReady;

          const allSummariesDone = executiveDone && compsDone && neighborhoodDone && neighborhoodDataDone;

          // Trigger completion ONLY when:
          // 1. All jobs are done (or no jobs exist and north is ready)
          // 2. If there are summary jobs, their content/timestamps must be updated
          //    (prevents finishing before OpenAI outputs are saved)
          // 3. Requires hasSeenProcessing to prevent old-state false positives
          const shouldComplete = hasSeenProcessingRef.current && allJobsDone && (
            hasAnySummaryJobs
              ? (allSummariesDone || isNorthReady)
              : (allSummariesDone || isNorthReady)
          );
          
          // Fallback: if backend reports processing finished, force completion even if some job statuses lag.
          // Requires hasSeenProcessing to prevent firing on the pre-refresh state (old completed jobs
          // + is_processing=false that exists while scrape-zillow is still running).
          const backendFinished = hasSeenProcessingRef.current && isNorthReady;

          if ((shouldComplete || backendFinished) && !isCompleteRef.current && !onCompleteCalledRef.current) {
            logger.debug('All jobs and summaries complete!', {
              allJobsDone,
              allSummariesDone,
              isNorthReady,
              executiveDone,
              compsDone,
              neighborhoodDone,
              summariesUpdated,
              hasExecutiveSummaryJob,
              hasCompsSummaryJob,
              hasNeighborhoodSummaryJob
            });
            isCompleteRef.current = true;
            setIsComplete(true);
            onCompleteCalledRef.current = true; // Mark as called to prevent duplicates
            if (onComplete) {
              // Call onComplete immediately, don't wait
              onComplete();
            }
          }
        }
      } catch (error) {
        logger.error('Error in pollJobs:', error);
      }
    };

    // Poll immediately
    pollJobs();

    // Poll every 2 seconds to catch job status updates and summary timestamp updates
    // Using 2 seconds to reduce load and avoid rapid state updates
    pollInterval = setInterval(() => {
      // Use refs to check completion/timeout status to avoid dependency issues
      if (!isCompleteRef.current && !hasTimedOutRef.current) {
        pollJobs();
      } else {
        // Clear interval if complete or timed out
        if (pollInterval) {
          clearInterval(pollInterval);
        }
      }
    }, 2000);

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [northId, onComplete, initialTimestamps]); // Removed isComplete from deps - using ref check instead

  const getJobStatus = (jobType: string): 'pending' | 'processing' | 'completed' | 'failed' | 'not-started' => {
    // Handle pseudo "scraping_property" job
    if (jobType === 'scraping_property') {
      // Once scraping is marked complete, it stays complete
      if (scrapingComplete) {
        return 'completed';
      }
      // If there are no jobs yet, scraping is still in progress
      if (jobs.length === 0) {
        return 'processing';
      }
      // Once jobs appear, scraping is complete
      return 'completed';
    }

    // For summary jobs, check if the summary has actually been updated (not just job status)
    const isSummaryJob = jobType === 'generate_executive_summary' || 
                         jobType === 'generate_comps_summary' || 
                         jobType === 'generate_neighborhood_summary';
    
    if (isSummaryJob) {
      // Check if job is completed
      const job = jobs.find(j => j.job_type === jobType);
      if (!job) {
        return 'not-started';
      }
      
      if (job.status === 'failed') {
        return 'failed';
      }
      
      // For summary jobs, only mark as completed if:
      // 1. Job status is completed AND
      // 2. The summary timestamp has actually been updated
      if (job.status === 'completed' || completedJobTypes.has(jobType)) {
        // Check if the corresponding summary has been updated
        let summaryUpdated = false;
        if (jobType === 'generate_executive_summary') {
          summaryUpdated = summariesUpdated.executive;
        } else if (jobType === 'generate_comps_summary') {
          summaryUpdated = summariesUpdated.comps;
        } else if (jobType === 'generate_neighborhood_summary') {
          summaryUpdated = summariesUpdated.neighborhood;
        }
        
        // Only return completed if summary is actually updated
        if (summaryUpdated) {
          return 'completed';
        } else {
          // Job is done but summary not saved yet - show as processing (yellow)
          return 'processing';
        }
      }
      
      // Job is still processing
      return job.status;
    }

    // For non-summary jobs, use the standard logic
    // Once a job is in completedJobTypes, it stays completed - never flip back
    if (completedJobTypes.has(jobType)) {
      // Check current jobs to see if it's failed, otherwise it's completed
      const currentJob = jobs.find(j => j.job_type === jobType);
      if (currentJob?.status === 'failed') {
        return 'failed';
      }
      // Always return completed if it's in the completed set
      return 'completed';
    }

    // For jobs not yet completed, check current status
    const job = jobs.find(j => j.job_type === jobType);
    if (!job) {
      return 'not-started';
    }
    return job.status;
  };

  const getJobIcon = (jobType: string) => {
    const status = getJobStatus(jobType);

    switch (status) {
      case 'completed':
        return (
          <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
            <Check className="h-3 w-3 text-white" />
          </div>
        );
      case 'processing':
        return (
          <div className="h-5 w-5 rounded-full border-2 border-primary flex items-center justify-center">
            <Loader2 className="h-3 w-3 text-primary animate-spin" />
          </div>
        );
      case 'failed':
        return (
          <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
            <X className="h-3 w-3 text-white" />
          </div>
        );
      case 'pending':
        return (
          <div className="h-5 w-5 rounded-full border-2 border-yellow-500 flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-yellow-500 animate-pulse" />
          </div>
        );
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-muted" />;
    }
  };

  const getTextColor = (jobType: string) => {
    const status = getJobStatus(jobType);

    switch (status) {
      case 'completed':
        return 'text-green-700 font-medium';
      case 'processing':
        return 'text-foreground font-medium';
      case 'failed':
        return 'text-red-600 font-medium';
      case 'pending':
        return 'text-yellow-700 font-medium';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onDismiss?.()}>
      <DialogContent className="sm:max-w-[500px] gap-8">
        <DialogHeader className="sr-only">
          <DialogTitle>Building your report</DialogTitle>
          <DialogDescription>Creating your professional seller report</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center space-y-6 py-6">
          <div className="relative">
            <Compass className={`h-16 w-16 text-primary ${!isComplete && !hasTimedOut ? 'animate-spin' : ''}`} style={{ animationDuration: "3s" }} />
          </div>

          <div className="space-y-2 text-center">
            <h2 className="text-2xl font-bold text-foreground">
              {isComplete ? 'Report Created!' : hasTimedOut ? 'Taking Longer Than Expected' : 'Building Your North Report'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isComplete
                ? 'All data has been gathered successfully'
                : hasTimedOut
                ? 'Your report may still be processing in the background. You can view it now — any remaining data will appear as it completes.'
                : 'Gathering property data and market intelligence'}
            </p>
          </div>

          <div className="w-full space-y-4">
            {JOB_ORDER.map((jobType) => (
              <div key={jobType} className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {getJobIcon(jobType)}
                </div>
                <p className={`text-sm ${getTextColor(jobType)}`}>
                  {JOB_DISPLAY_NAMES[jobType] || jobType}
                </p>
              </div>
            ))}
          </div>

          {!hasTimedOut && (
            <p className="text-xs text-center text-muted-foreground">
              Finalizing updates. This will close automatically when ready.
            </p>
          )}

          {(isComplete || hasTimedOut) && onDismiss && (
            <Button
              onClick={onDismiss}
              className="w-full"
            >
              View Report
            </Button>
          )}

          {!isComplete && !hasTimedOut && onDismiss && (
            <p className="text-xs text-center text-muted-foreground">
              You can close this dialog and we'll continue building in the background
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
